import type { ShipConfirmationStage, ShipConfirmationTracker } from '../api/types'

/**
 * The kanban board's unit of display — one card per PO, even though the
 * underlying `po_shipment_confirmation` rows are tracked per PO *line*
 * (2026-08-26 rework, "some material will have different dates than
 * others"). `stage` is the aggregate column: a PO still needs follow-up
 * (shows under Awaiting Confirmation) as long as ANY of its lines does;
 * it only shows under Confirmed once every line does.
 */
export interface PoGroup {
  odoo_po_id: number
  odoo_po_name: string
  vendor_name: string | null
  lines: ShipConfirmationTracker[]
  stage: ShipConfirmationStage
  confirmedCount: number
  totalCount: number
  /** Earliest `created_at` across the group's lines — how long the oldest
   *  still-open line has been sitting, used for the card's "Open Nd". */
  earliestCreatedAt: string
}

/**
 * Groups a flat list of tracker rows into one `PoGroup` per `odoo_po_id`.
 * Only ever sees `awaiting_confirmation`/`confirmed` rows in practice —
 * the board's own list fetch already filters to `BOARD_STAGES` — so the
 * aggregate `stage` only needs to choose between those two.
 */
export function groupTrackersByPo(trackers: ShipConfirmationTracker[]): PoGroup[] {
  const byPo = new Map<number, ShipConfirmationTracker[]>()
  for (const tracker of trackers) {
    const existing = byPo.get(tracker.odoo_po_id)
    if (existing) {
      existing.push(tracker)
    } else {
      byPo.set(tracker.odoo_po_id, [tracker])
    }
  }

  return Array.from(byPo.values()).map((lines): PoGroup => {
    const first = lines[0]
    const confirmedCount = lines.filter((l) => l.stage === 'confirmed').length
    const stage: ShipConfirmationStage =
      confirmedCount === lines.length ? 'confirmed' : 'awaiting_confirmation'
    const earliestCreatedAt = lines.reduce(
      (earliest, l) => (l.created_at < earliest ? l.created_at : earliest),
      first.created_at,
    )
    return {
      odoo_po_id: first.odoo_po_id,
      odoo_po_name: first.odoo_po_name,
      vendor_name: first.vendor_name,
      lines,
      stage,
      confirmedCount,
      totalCount: lines.length,
      earliestCreatedAt,
    }
  })
}
